import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'

import useMutation from '@hooks/useGQLMutation'
import { sendSmsMutation } from './mutations'
import { useAlertsStore } from '@stores/alertsStore'
import { FormDialog } from '@components/molecules/FormDialog'

import { i18nValidation, scopedTranslation } from '@frontend/utils/I18n'
import { Textarea } from '@atoms/form/Textarea'
import type { SendSmsDialogState } from '../../types/dialogTypes'

const t = scopedTranslation('components.send_sms_dialog')
const tAttributes = scopedTranslation('attributes.individual_sms')

const ALERTS_PATH = 'sendSms' as const

const schema = yup.object({
  body: yup
    .string()
    .label(tAttributes('body'))
    .required(i18nValidation('required'))
}).required()

type FormData = {
  body: string
}

type SendSmsDialogProps = {
  onClose: () => void
  dialogState: SendSmsDialogState
}

export function SendSmsDialog({
  onClose,
  dialogState,
}: SendSmsDialogProps) {
  const { dialogData } = dialogState
  const supporterId = dialogData.id
  const supporterName = `${dialogData.firstName || ''} ${dialogData.lastName || ''}`.trim()
  const groupId = dialogData.groupId
  const { setAlerts, clearAlerts } = useAlertsStore(({ setAlerts, clearAlerts }) => ({
    setAlerts,
    clearAlerts,
  }))

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm<FormData>({
    resolver: yupResolver(schema)
  })

  const sendSmsMutationHook = useMutation(sendSmsMutation)

  const onSubmit = (data: FormData) => {
    clearAlerts(ALERTS_PATH)
    sendSmsMutationHook.mutate({
      supporterId,
      groupId,
      smsBody: data.body,
    }, {
      onSuccess: (response) => {
        if (response?.sendSms?.success) {
          setAlerts({
            alertsPath: ALERTS_PATH,
            alerts: [{
              type: 'success',
              details: {
                title: t('alerts.success.title')
              }
            }]
          })
          reset()
        } else {
          setAlerts({
            alertsPath: ALERTS_PATH,
            alerts: response?.sendSms?.errors?.map(error => ({
              type: 'error',
              details: {
                title: error.message
              }
            })) || []
          })
        }
      },
      onError: () => {
        setAlerts({
          alertsPath: ALERTS_PATH,
          alerts: [{
            type: 'error',
            details: {
              title: t('errors.send_failed')
            }
          }]
        })
      }
    })
  }

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      reset()
      clearAlerts(ALERTS_PATH)
      onClose()
    }
  }

  return (
    <FormDialog.Root 
      open={true}
      onOpenChange={handleOpenChange}
      testId="send-sms-dialog"
      preventOutsideClick
    >
      <FormDialog.Title alertsPath={ALERTS_PATH}>{t('title', { name: supporterName })}</FormDialog.Title>
      <FormDialog.Form onSubmit={handleSubmit(onSubmit)}>
        <FormDialog.Body>
          <Textarea
            label={t('form.body_label')}
            errors={errors}
            placeholder={t('form.body_description')}
            {...register('body')}
          />
        </FormDialog.Body>
        <FormDialog.Footer 
          submitText={t('form.send_now')} 
          isLoading={sendSmsMutationHook.isLoading}
        />
      </FormDialog.Form>
    </FormDialog.Root>
  )
}